// Marketplace listing types for Community Marketplace feature

export type ListingCondition = 'new' | 'new_without_tags' | 'like_new' | 'used' | 'fair';

// Condition options specifically for the Post Item form (matches Figma design)
export type PostItemCondition = 'brand_new' | 'new_without_tags' | 'used';

export type PickupOption = 'room_pickup' | 'lobby_pickup' | 'any_pickup';

// Pickup options for the Post Item form (multi-select checkboxes)
export type PostItemPickupOption = 'room_pickup' | 'lobby_pickup' | 'meet_in_person';

// Price type for Post Item form
export type PostItemPriceType = 'set_price' | 'free';

// Media item for the Post Item form
export interface PostItemMediaItem {
  id: number;           // Local ID for React key (can be index or timestamp)
  uri: string;
  type: string;
  name?: string;
  size?: number;
  file?: File;          // For web uploads
  serverId?: number;    // Backend database ID for existing images (used during edit)
}

// Image object returned from backend
export interface ListingImage {
  id: number;           // Database ID
  url: string;          // Image URL
}

// Form data for posting a new item
export interface PostItemFormData {
  title: string;
  description: string;
  images: PostItemMediaItem[];
  priceType: PostItemPriceType;
  price: string;
  condition: PostItemCondition;
  pickupOptions: PostItemPickupOption[];
}

export interface MarketplaceSeller {
  id: string;
  name: string;
  initials: string;
  avatarUrl?: string;
  rating: number; // 0-5
  reviewCount: number;
}

// Extended seller profile with stats for Seller Profile screen
export interface SellerProfile extends MarketplaceSeller {
  bought: number;      // Number of items purchased
  sold: number;        // Number of items sold
  followers: number;   // Follower count
  following: number;   // Following count
  isFollowing: boolean; // Whether current user follows this seller
  listings: MarketplaceListing[]; // Seller's active listings
}

export interface MarketplaceListing {
  id: string;
  title: string;
  description: string;
  price: string; // Formatted price string (e.g., "$200", "Free")
  priceValue: number; // Numeric value for sorting/filtering
  condition: ListingCondition;
  images: ListingImage[]; // Array of image objects with id and url
  pickupOption: PickupOption;
  seller: MarketplaceSeller;
  isBookmarked: boolean;
  createdAt: string;
  updatedAt: string;
}

// Helper to get display text for condition
export const getConditionDisplayText = (condition: ListingCondition): string => {
  switch (condition) {
    case 'new':
      return 'New';
    case 'new_without_tags':
      return 'New without tags';
    case 'like_new':
      return 'Like New';
    case 'used':
      return 'Used';
    case 'fair':
      return 'Fair';
    default:
      return 'Used';
  }
};

// Helper to get display text for pickup option
export const getPickupOptionDisplayText = (option: PickupOption): string => {
  switch (option) {
    case 'room_pickup':
      return 'Room pickup';
    case 'lobby_pickup':
      return 'Pickup at lobby';
    case 'any_pickup':
      return 'Room pickup • Pickup at lobby • Meet in person';
    default:
      return 'Room pickup';
  }
};

// Helper to get display text for post item condition
export const getPostItemConditionDisplayText = (condition: PostItemCondition): string => {
  switch (condition) {
    case 'brand_new':
      return 'Brand new';
    case 'new_without_tags':
      return 'New without tags';
    case 'used':
      return 'Used';
    default:
      return 'Used';
  }
};

// Helper to get display text for post item pickup option
export const getPostItemPickupDisplayText = (option: PostItemPickupOption): string => {
  switch (option) {
    case 'room_pickup':
      return 'Room pickup';
    case 'lobby_pickup':
      return 'Pickup at lobby';
    case 'meet_in_person':
      return 'Meet in person';
    default:
      return 'Room pickup';
  }
};

// Initial form data for posting an item
export const INITIAL_POST_ITEM_FORM: PostItemFormData = {
  title: '',
  description: '',
  images: [],
  priceType: 'set_price',
  price: '',
  condition: 'brand_new',
  pickupOptions: [],
};

// ============================================================================
// My Listings / Marketplace Profile Types
// ============================================================================

// Status of user's own listing
export type MyListingStatus = 'active' | 'sold';

// Extended listing type for user's own listings (My Profile screen)
export interface MyListing extends MarketplaceListing {
  status: MyListingStatus;
  views: number;       // Number of views on the listing
  saved: number;       // Number of times saved/bookmarked by others
  soldAt?: string;     // ISO date string when item was marked as sold
}

// User's marketplace statistics for profile screen
export interface UserMarketplaceStats {
  bought: number;      // Number of items purchased
  sold: number;        // Number of items sold
  followers: number;   // Follower count
  following: number;   // Following count
  rating: number;      // Average rating (0-5)
  reviewCount: number; // Number of reviews received
}

// ============================================================================
// Following Users Types
// ============================================================================

// User being followed in the marketplace
export interface FollowingUser {
  id: string;
  name: string;
  initials: string;
  avatarUrl?: string;
  hasNewListing: boolean; // Orange dot indicator for users with new listings
  isFollowing: boolean;
}

// ============================================================================
// Feedback / Reviews Types
// ============================================================================

// Feedback/review item received by the user
export interface FeedbackItem {
  id: string;
  rating: number; // 1-5 stars
  itemTitle: string; // Product that was purchased
  itemImageUrl?: string; // Product image URL
  reviewText?: string; // Optional review comment
  reviewerName: string;
  reviewerInitials: string;
  reviewerAvatarUrl?: string;
  date: string; // ISO date string
}

// Helper to format feedback date for display
export const formatFeedbackDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ============================================================================
// Listing Buyers Types (for sold feedback flow)
// ============================================================================

// Buyer who has messaged about a listing
export interface ListingBuyer {
  id: string;
  name: string;
  initials: string;
  avatarUrl?: string;
}

// ============================================================================
// Recently Viewed Types
// ============================================================================

// Recently viewed item for marketplace home screen
export interface RecentlyViewedItem {
  id: string;
  image: string;
  listing: MarketplaceListing;
}

// ============================================================================
// Report Types
// ============================================================================

// Report reason options
export type ReportReason = 'spam' | 'inappropriate' | 'harassment';

// Report reason display text
export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam content' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'harassment', label: 'Harassment' },
];

// Report submission data
export interface ReportSubmission {
  listingId: string;
  reason: ReportReason;
  description?: string;
}

// ============================================================================
// Saved Listings Types
// ============================================================================

// Extended listing type for user's saved/bookmarked items
export interface SavedListing extends MarketplaceListing {
  isSold: boolean;     // Whether the item has been sold
  soldAt?: string;     // ISO date string when item was sold (if applicable)
}

// ============================================================================
// Inbox / Conversation Types
// ============================================================================

// Simplified listing info for inbox conversations
export interface InboxListingInfo {
  id: string;
  title: string;
  image: string;
}

// User info for conversation participants
export interface ConversationUser {
  id: string;
  name: string;
  initials: string;
  avatarUrl?: string;
}

// Last message preview for inbox list
export interface LastMessagePreview {
  text: string;
  timestamp: string; // ISO date string
  isFromMe: boolean;
}

// Inbox conversation (for listing in inbox screen)
export interface InboxConversation {
  id: string;
  listing: InboxListingInfo;
  otherUser: ConversationUser;
  lastMessage: LastMessagePreview;
  isUnread: boolean;
}

// File attachment in a message
export interface MessageFile {
  id: string;
  url: string;
  name: string;
}

// Reply-to message info
export interface ReplyToInfo {
  id: string;
  text: string;
  senderName: string;
}

// Individual message in a conversation
export interface ConversationMessage {
  id: string;
  text: string;
  sender: 'user' | 'other';
  timestamp: string | Date; // Can be ISO string (from Redux) or Date object
  files?: MessageFile[];
  replyTo?: ReplyToInfo;
}

// Full conversation data for conversation screen
export interface ConversationDetail {
  id: string;
  listing: InboxListingInfo;
  otherUser: ConversationUser;
  messages: ConversationMessage[];
}

// Helper to format conversation date for display
export const formatConversationDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

// Helper to convert MyListing to PostItemFormData for editing
export const myListingToFormData = (listing: MyListing): PostItemFormData => {
  // Map listing condition to post item condition
  let formCondition: PostItemCondition = 'used';
  if (listing.condition === 'new' || listing.condition === 'like_new') {
    formCondition = 'brand_new';
  } else if (listing.condition === 'new_without_tags') {
    formCondition = 'new_without_tags';
  }

  // Map pickup option to form pickup options
  const pickupOptions: PostItemPickupOption[] = [];
  if (listing.pickupOption === 'room_pickup') {
    pickupOptions.push('room_pickup');
  } else if (listing.pickupOption === 'lobby_pickup') {
    pickupOptions.push('lobby_pickup');
  } else if (listing.pickupOption === 'any_pickup') {
    pickupOptions.push('room_pickup', 'lobby_pickup', 'meet_in_person');
  }

  return {
    title: listing.title,
    description: listing.description,
    images: listing.images.map((img, index) => ({
      id: index,
      uri: img.url,
      type: 'image/jpeg',
      serverId: img.id, // Preserve backend image ID for edit operations
    })),
    priceType: listing.priceValue === 0 ? 'free' : 'set_price',
    price: listing.priceValue === 0 ? '' : listing.priceValue.toString(),
    condition: formCondition,
    pickupOptions,
  };
};

